/**
 * Feature 4 (2026-05-28): React Query hooks for Agreement Tasks.
 */
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { agreementTasksApi, type AgreementTaskMyCounts } from '../api/agreementTasks';
import { ApiError } from '../api/client';
import type {
  AgreementTask, AgreementTaskCreateInput, AgreementTaskStatus,
  AgreementTaskUpdateInput, Page, UUID,
} from '../api/types';

const keys = {
  all: ['agreement-tasks'] as const,
  list: (agreementId?: UUID, statuses?: AgreementTaskStatus[], includeDeleted?: boolean,
    urgency?: number[], sort?: string, limit?: number, offset?: number) =>
    ['agreement-tasks', 'list', agreementId, statuses, !!includeDeleted, urgency,
      sort, limit, offset] as const,
  myList: (statuses?: AgreementTaskStatus[]) =>
    ['agreement-tasks', 'my-list', statuses] as const,
  detail: (id: UUID) => ['agreement-tasks', 'detail', id] as const,
  myCounts: ['agreement-tasks', 'my-counts'] as const,
};

export function useAgreementTasks(
  agreementId: UUID | undefined,
  opts?: {
    statuses?: AgreementTaskStatus[]; include_deleted?: boolean; urgency?: number[];
    /** P41: header sort + paging, passed through to the server. */
    sort?: string; limit?: number; offset?: number;
  },
) {
  const statuses = opts?.statuses;
  const includeDeleted = !!opts?.include_deleted;
  const urgency = opts?.urgency?.length ? opts.urgency : undefined;
  const limit = opts?.limit ?? 50;
  const offset = opts?.offset ?? 0;
  return useQuery<Page<AgreementTask>, ApiError>({
    queryKey: keys.list(agreementId, statuses, includeDeleted, urgency, opts?.sort, limit, offset),
    queryFn: () => agreementTasksApi.list({
      agreement_id: agreementId!,
      statuses,
      include_deleted: includeDeleted,
      urgency,
      sort: opts?.sort,
    }, limit, offset),
    enabled: !!agreementId,
    placeholderData: keepPreviousData,
  });
}

export function useMyAgreementTasks(opts?: {
  statuses?: AgreementTaskStatus[];
  mine?: boolean;
  include_deleted?: boolean;
  /** #63: only "General" (no-agreement) tasks. */
  general?: boolean;
  // #47 (2026-06-12): per-column predicate filters (C-132), server-side.
  // People axes are multi-select since C-132 Amendment 3.
  q?: string;
  assignee_user_id?: UUID[];
  created_by_user_id?: UUID[];
  deadline_from?: string;
  deadline_to?: string;
  agreement_q?: string;
  /** #63: exact parent-agreement filter (the Related-to combobox selection). */
  agreement_id?: UUID;
  /** P14: urgency levels to keep (1 High, 2 Medium, 3 Low). */
  urgency?: number[];
  /** P42: Primary Named RM user ids. */
  rm_user_id?: UUID[];
  /** P41: header sort + paging, passed through to the server. */
  sort?: string;
  limit?: number;
  offset?: number;
}) {
  const statuses = opts?.statuses;
  const limit = opts?.limit ?? 50;
  const offset = opts?.offset ?? 0;
  // Default to mine=true for the per-user view; admin can pass false to
  // fetch all tasks across the firm (matches the /my-counts scope=all).
  const mine = opts?.mine !== false;
  const includeDeleted = !!opts?.include_deleted;
  return useQuery<Page<AgreementTask>, ApiError>({
    // #47: filter values are part of the key (C-131).
    queryKey: ['agreement-tasks', 'my-list', statuses, mine, includeDeleted,
      { q: opts?.q, assignee: opts?.assignee_user_id, creator: opts?.created_by_user_id,
        from: opts?.deadline_from, to: opts?.deadline_to, ag: opts?.agreement_q,
        general: opts?.general, aid: opts?.agreement_id, urgency: opts?.urgency, rm: opts?.rm_user_id,
        sort: opts?.sort, limit, offset }],
    queryFn: () => agreementTasksApi.list({
      mine, statuses, include_deleted: includeDeleted,
      q: opts?.q || undefined,
      assignee_user_id: opts?.assignee_user_id?.length ? opts.assignee_user_id : undefined,
      created_by_user_id: opts?.created_by_user_id?.length ? opts.created_by_user_id : undefined,
      deadline_from: opts?.deadline_from || undefined,
      deadline_to: opts?.deadline_to || undefined,
      agreement_q: opts?.agreement_q || undefined,
      general: opts?.general || undefined,
      agreement_id: opts?.agreement_id || undefined,
      urgency: opts?.urgency?.length ? opts.urgency : undefined,
      rm_user_id: opts?.rm_user_id?.length ? opts.rm_user_id : undefined,
      sort: opts?.sort,
    }, limit, offset),
    refetchOnMount: 'always',
    refetchInterval: 60_000,
    staleTime: 0,
    placeholderData: keepPreviousData,
  });
}

export function useMyTaskCounts() {
  return useQuery<AgreementTaskMyCounts, ApiError>({
    queryKey: keys.myCounts,
    queryFn: () => agreementTasksApi.myCounts(),
    refetchOnMount: 'always',
    refetchInterval: 60_000,
    staleTime: 0,
  });
}

export function useCreateAgreementTask() {
  const qc = useQueryClient();
  return useMutation<AgreementTask, ApiError, AgreementTaskCreateInput>({
    mutationFn: (input) => agreementTasksApi.create(input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['agreement-tasks'] });
    },
  });
}

export function useUpdateAgreementTask() {
  const qc = useQueryClient();
  return useMutation<AgreementTask, ApiError, { id: UUID; input: AgreementTaskUpdateInput }>({
    mutationFn: ({ id, input }) => agreementTasksApi.update(id, input),
    onSuccess: (t) => {
      qc.invalidateQueries({ queryKey: ['agreement-tasks'] });
      qc.setQueryData(keys.detail(t.id), t);
    },
  });
}

export function useUpdateAgreementTaskStatus() {
  const qc = useQueryClient();
  return useMutation<AgreementTask, ApiError, { id: UUID; status: AgreementTaskStatus }>({
    mutationFn: ({ id, status }) => agreementTasksApi.updateStatus(id, status),
    onSuccess: (t) => {
      qc.invalidateQueries({ queryKey: ['agreement-tasks'] });
      qc.setQueryData(keys.detail(t.id), t);
    },
  });
}

export function useDeleteAgreementTask() {
  const qc = useQueryClient();
  return useMutation<AgreementTask, ApiError, UUID>({
    mutationFn: (id) => agreementTasksApi.delete(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['agreement-tasks'] });
      qc.invalidateQueries({ queryKey: ['change-requests'] });
    },
  });
}

export function useRestoreAgreementTask() {
  const qc = useQueryClient();
  return useMutation<AgreementTask, ApiError, UUID>({
    mutationFn: (id) => agreementTasksApi.restore(id),
    onSuccess: (t) => {
      qc.invalidateQueries({ queryKey: ['agreement-tasks'] });
      qc.invalidateQueries({ queryKey: ['change-requests'] });
      qc.setQueryData(keys.detail(t.id), t);
    },
  });
}
